import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getProperty, getPropertyImages, createLoan, formatCurrency } from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { formatDueDayOptions, formatOrdinalDay } from '../utils/formatters';
// WHERE THE PROCESSING FEE GOES. The browser twin of server/lib/fees.js: the
// page quotes from the same rule the purchase endpoint charges from, so the
// figure a buyer is shown and the figure their card is given cannot disagree.
import { feeBreakdown } from '../utils/fees';

// Business Settings stores a financing tier as EITHER a fixed dollar amount or a
// percentage, leaving the other field null. Number(null) is 0, so null has to be
// rejected before coercion or an unused field reads as a real "0 down" option.
const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// Helper function to transform Cloudinary URLs
const getCloudinaryTransform = (url, width, height = null, quality = 'auto') => {
  if (!url || !url.includes('cloudinary.com')) return url;
  
  const parts = url.split('/upload/');
  if (parts.length === 2) {
    const transforms = [`w_${width}`, 'c_fill', `q_${quality}`, 'f_auto'];
    if (height) transforms.splice(1, 0, `h_${height}`);
    return `${parts[0]}/upload/${transforms.join(',')}/${parts[1]}`;
  }
  return url;
};

// THE BUYER'S IN-PROGRESS PURCHASE, PARKED ACROSS THE LOGIN ROUND TRIP.
//
// This funnel is browse-freely, auth-at-checkout: an unauthenticated buyer can
// open the checkout form and fill in every field, and only discovers at "Pay
// $X Now" that they need an account. That redirect unmounts this page, which
// used to discard everything they had typed. The draft is written at that one
// moment -- the bounce -- and read back when they return.
//
// TWO KINDS OF THING ARE DELIBERATELY NOT IN THE DRAFT.
//
// Card details, which never can be: the PAN lives in Square's iframe, which
// this origin cannot read, and it is gone on unmount by design. That is
// correct and stays.
//
// The Terms of Service and Due Diligence acknowledgements, which could be
// restored and are not. They are legal affirmations, and the interruption they
// have to survive includes CREATING THE ACCOUNT that the resulting contract is
// written against -- so a restored checkbox would be an affirmation made by a
// visitor before the buyer existed. Re-checking two boxes costs the buyer
// seconds and removes any argument that they never affirmed post-signup.
// Everything the buyer TYPED still restores; only the two acknowledgements
// reset, and the existing `disabled` expression on the pay button already
// blocks submission until both are re-checked.
//
// sessionStorage, not localStorage: this is a single purchase attempt in a
// single tab, and it should not outlive the tab. Each tenant is its own origin
// (subdomain), so one tenant's draft is not reachable from another's.
const PURCHASE_DRAFT_PREFIX = 'deedstack:purchase-draft:';
// Long enough to read the terms and create an account from scratch, short
// enough that a shared or kiosk browser left open does not hand the next
// person a name, phone and home address hours later.
const PURCHASE_DRAFT_TTL_MS = 30 * 60 * 1000;

const purchaseDraftKey = (propertyId) => `${PURCHASE_DRAFT_PREFIX}${propertyId}`;

// Every one of these swallows its own failure. Storage can be disabled, full,
// or blocked by privacy settings, and a buyer in that state must still be able
// to complete the purchase -- they just lose the convenience of the round trip.
const savePurchaseDraft = (propertyId, draft) => {
  try {
    sessionStorage.setItem(
      purchaseDraftKey(propertyId),
      JSON.stringify({ savedAt: Date.now(), draft })
    );
  } catch (err) {
    console.warn('[purchase] could not save draft:', err);
  }
};

const readPurchaseDraft = (propertyId) => {
  try {
    const raw = sessionStorage.getItem(purchaseDraftKey(propertyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt);
    // An unreadable or expired draft is removed rather than left to rot: the
    // expiry has to actually delete the data, not merely decline to use it.
    if (!Number.isFinite(savedAt) || Date.now() - savedAt > PURCHASE_DRAFT_TTL_MS) {
      sessionStorage.removeItem(purchaseDraftKey(propertyId));
      return null;
    }
    return parsed.draft || null;
  } catch (err) {
    console.warn('[purchase] could not read draft:', err);
    return null;
  }
};

// Drops every parked draft except, optionally, the one being resumed. Keyed per
// property so one listing's details can never surface on another; purging the
// rest on arrival is what makes "cleared on property change" true however the
// buyer got here -- including the back button, which does not remount the page.
const clearPurchaseDrafts = (keepPropertyId = null) => {
  try {
    const keep = keepPropertyId === null ? null : purchaseDraftKey(keepPropertyId);
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PURCHASE_DRAFT_PREFIX) && k !== keep) {
        sessionStorage.removeItem(k);
      }
    }
  } catch (err) {
    console.warn('[purchase] could not clear drafts:', err);
  }
};

function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { getCompanyLegalName, getCompanyEmail, getCompanyPhone, getFinancingTiers, getMinimumMonthlyPayment, getProcessingFee, getTermOptions, getCurePeriodDays, getGracePeriodDays, getPaymentDueDayMode, getPaymentDueDayOptions, getSquareApplicationId, getSquareLocationId, getSquareEnvironment, hasCustomSquareCredentials } = useConfig();
  const financingTiers = getFinancingTiers();
  const minimumPayment = getMinimumMonthlyPayment();
  const processingFee = getProcessingFee();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Calculator state
  const [downPaymentOption, setDownPaymentOption] = useState('99');
  const [termMonths, setTermMonths] = useState(36);
  const [calculation, setCalculation] = useState(null);
  const [desiredPayment, setDesiredPayment] = useState('');
  const [billingInfo, setBillingInfo] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  const [deedInfo, setDeedInfo] = useState({
    deedName: '',
    deedMailingAddress: ''
  });
  const [paymentDueDay, setPaymentDueDay] = useState('1');

  // Tenant config arrives asynchronously, so the initial '1' above can turn out
  // to be a day this tenant does not offer. Snap to the first offered day rather
  // than letting the buyer submit a due day the server will reject.
  //
  // Skipped entirely in anniversary mode: there is no picker, the server derives
  // the day from the closing date and ignores whatever the body carries, so
  // snapping to a day from a list nobody is offered would be meaningless.
  const dueDayMode = getPaymentDueDayMode();
  const isAnniversaryDueDay = dueDayMode === 'anniversary';
  const offeredDueDays = getPaymentDueDayOptions();
  useEffect(() => {
    if (isAnniversaryDueDay) return;
    if (!Array.isArray(offeredDueDays) || offeredDueDays.length === 0) return;
    if (!offeredDueDays.map(String).includes(paymentDueDay)) {
      setPaymentDueDay(String(offeredDueDays[0]));
    }
  }, [isAnniversaryDueDay, offeredDueDays, paymentDueDay]);
  const [dueDiligenceAgreed, setDueDiligenceAgreed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);

  // Dynamically load Square SDK based on tenant's environment
  useEffect(() => {
    // Only load if tenant has Square credentials configured
    if (!hasCustomSquareCredentials()) {
      return;
    }

    const loadSquareSDK = () => {
      // Check if already loaded
      if (window.Square) {
        setSquareLoaded(true);
        return;
      }

      const environment = getSquareEnvironment();
      const isProduction = environment === 'production';

      const script = document.createElement('script');
      script.src = isProduction
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js';
      script.async = true;

      script.onload = () => {
        console.log('[Square] SDK loaded successfully');
        setSquareLoaded(true);
      };

      script.onerror = () => {
        console.error('[Square] Failed to load SDK');
        setPurchaseError('Failed to load payment system. Please refresh and try again.');
      };

      document.body.appendChild(script);
    };

    loadSquareSDK();
  }, [hasCustomSquareCredentials, getSquareEnvironment]);

  // Resolve which configured tier a selection refers to. This mirrors the
  // server's findFinancingTier exactly, including the precedence: fixed-dollar
  // tiers are matched first, so the value 99 means "$99 down", not "99% down".
  // There is no nearest-tier fallback here on purpose - the server rejects
  // anything that is not an exact configured tier, so quoting a rate for an
  // unconfigured option would just advertise a price it will refuse.
  const resolveTier = useCallback((selection) => {
    const value = toNumberOrNull(selection);
    if (value === null) return null;

    const fixed = financingTiers.find(t => toNumberOrNull(t?.downPayment) === value);
    if (fixed) return { tier: fixed, kind: 'fixed', apr: toNumberOrNull(fixed.apr) };

    const percent = financingTiers.find(t => toNumberOrNull(t?.downPaymentPercent) === value);
    if (percent) return { tier: percent, kind: 'percent', apr: toNumberOrNull(percent.apr) };

    return null;
  }, [financingTiers]);

  // The options actually on offer. The calculator renders exactly these, so it
  // can never present terms the server will reject.
  const fixedTier = useMemo(
    () => financingTiers.find(t => toNumberOrNull(t?.downPayment) !== null) || null,
    [financingTiers]
  );
  // A fixed tier's selection value is its dollar amount, which is what the
  // server matches against tier.downPayment.
  const fixedOption = fixedTier ? String(toNumberOrNull(fixedTier.downPayment)) : null;
  const percentOptions = useMemo(
    () => financingTiers
      .map(t => toNumberOrNull(t?.downPaymentPercent))
      .filter(p => p !== null)
      .sort((a, b) => a - b)
      .map(String),
    [financingTiers]
  );
  const offeredOptions = useMemo(
    () => [...(fixedOption ? [fixedOption] : []), ...percentOptions],
    [fixedOption, percentOptions]
  );
  // termOptions are configured in YEARS; the API takes MONTHS.
  const termMonthsOptions = useMemo(
    () => getTermOptions()
      .map(toNumberOrNull)
      .filter(years => years !== null && years > 0)
      .map(years => Math.round(years * 12)),
    [getTermOptions]
  );

  // One amortization, used for every quote on this page. Returns null when the
  // selection is not a configured tier, which is also the answer the server gives.
  const quoteFor = useCallback((selection, months) => {
    const match = resolveTier(selection);
    const price = toNumberOrNull(property?.price);
    if (!match || match.apr === null || price === null || !months) return null;

    const downPayment = match.kind === 'fixed'
      ? toNumberOrNull(match.tier.downPayment)
      : price * (toNumberOrNull(match.tier.downPaymentPercent) / 100);
    if (downPayment === null) return null;

    // WHERE THE FEE GOES, from THIS property's own policy.
    //
    // This read `(price - downPayment) + processingFee` while the cost-today box
    // below separately showed `downPayment + processingFee`, so the page both
    // financed the fee and collected it. The single call now decides once and
    // hands back both figures.
    const fees = feeBreakdown({
      price,
      downPayment,
      processingFee,
      feeHandling: property?.fee_handling
    });
    const principal = fees.amountFinanced;
    const monthlyRate = match.apr / 100 / 12;
    let monthlyPayment = monthlyRate === 0
      ? principal / months
      : principal * (monthlyRate * Math.pow(1 + monthlyRate, months))
        / (Math.pow(1 + monthlyRate, months) - 1);
    monthlyPayment = Math.max(monthlyPayment, minimumPayment);

    return {
      tier: match.tier,
      kind: match.kind,
      interestRate: match.apr,
      downPayment,
      principal,
      // What is actually due today, and whether the fee is inside the principal
      // above. Exactly one of the two carries it.
      dueAtSigning: fees.dueAtSigning,
      feeFinanced: fees.feeFinanced,
      monthlyPayment,
      totalAmount: monthlyPayment * months
    };
  }, [resolveTier, property, processingFee, minimumPayment]);

  // Keep the selection inside what the business offers. Config loads async, so
  // the initial defaults may not be on the menu for this tenant.
  useEffect(() => {
    if (offeredOptions.length && !offeredOptions.includes(downPaymentOption)) {
      setDownPaymentOption(offeredOptions[0]);
    }
    if (termMonthsOptions.length && !termMonthsOptions.includes(termMonths)) {
      setTermMonths(termMonthsOptions[0]);
    }
  }, [offeredOptions, termMonthsOptions, downPaymentOption, termMonths]);

  // Find closest payment option to desired payment
  const findClosestOption = () => {
    if (!desiredPayment || desiredPayment < minimumPayment || !property) return null;

    const desired = parseFloat(desiredPayment);
    const options = offeredOptions
      .map(option => ({ option, quote: quoteFor(option, termMonths) }))
      .filter(o => o.quote)
      .map(o => ({ option: o.option, payment: o.quote.monthlyPayment }));

    if (!options.length) return null;

    return options.reduce(
      (best, opt) => Math.abs(opt.payment - desired) < Math.abs(best.payment - desired) ? opt : best,
      options[0]
    ).option;
  };

  const closestOption = findClosestOption();
  
  // Purchase state
  const [purchasing, setPurchasing] = useState(false);
  // Synchronous double-submit guard. `purchasing` only disables the button on
  // the next render, so two clicks dispatched before React re-renders would
  // both get through. A ref flips immediately.
  const submitLockRef = useRef(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [cardInstance, setCardInstance] = useState(null);
  // Set by the restore below, consumed by the card re-attach effect further
  // down. The card form cannot be brought up in the same pass as the fields.
  const [restoreCardPending, setRestoreCardPending] = useState(false);

  // Restore once per property, on arrival. Keyed by the id rather than by a
  // plain boolean because navigating from one listing to another reuses this
  // component without remounting -- a boolean would restore the first property
  // and then never run again.
  //
  // Consumed on arrival and not on every render, so a buyer who deliberately
  // clears a field does not get it typed back in underneath them.
  const restoredForIdRef = useRef(null);
  useEffect(() => {
    if (restoredForIdRef.current === id) return;
    restoredForIdRef.current = id;

    // Any other property's draft goes now -- the buyer has moved on.
    clearPurchaseDrafts(id);

    const draft = readPurchaseDraft(id);
    if (!draft) return;

    if (draft.billingInfo) setBillingInfo(prev => ({ ...prev, ...draft.billingInfo }));
    if (draft.deedInfo) setDeedInfo(prev => ({ ...prev, ...draft.deedInfo }));
    if (draft.paymentDueDay) setPaymentDueDay(String(draft.paymentDueDay));
    if (draft.downPaymentOption) setDownPaymentOption(String(draft.downPaymentOption));
    if (Number.isFinite(Number(draft.termMonths))) setTermMonths(Number(draft.termMonths));
    if (typeof draft.desiredPayment === 'string') setDesiredPayment(draft.desiredPayment);

    // A restored selection is not trusted over the tenant's current offer: the
    // two snap effects above still run and will pull downPaymentOption, term
    // and due day back onto a configured option if the business changed its
    // terms while the buyer was logging in.
    setRestoreCardPending(true);
  }, [id]);

  const loadProperty = useCallback(async () => {
    try {
      const response = await getProperty(id);
      setProperty(response.data);
    } catch (err) {
      setError('Failed to load property');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadImages = useCallback(async () => {
    try {
      const response = await getPropertyImages(id);
      setImages(response.data);
    } catch (err) {
      console.error('Failed to load images:', err);
    }
  }, [id]);

  useEffect(() => {
    loadProperty();
    loadImages();
  }, [id, loadProperty, loadImages]);

  // The tab title, for navigation WITHIN the app.
  //
  // The meta edge function already put this property's title into the document
  // that came from the CDN, and that is what a crawler or a link preview reads.
  // It cannot help a buyer who arrives here by clicking through from the
  // listings, because no document is fetched for that: React swaps the route and
  // the title stays whatever updateMetaTags last set, which is the tenant's own
  // name. So the two halves cover the two ways of arriving.
  useEffect(() => {
    if (!property?.title) return undefined;

    const previous = document.title;
    const company = getCompanyLegalName();
    document.title = company ? `${property.title} | ${company}` : property.title;

    // Put it back on the way out, so returning to the listings does not leave a
    // property title sitting above them.
    return () => { document.title = previous; };
  }, [property, getCompanyLegalName]);

  const calculatePayment = useCallback(() => {
    const quote = quoteFor(downPaymentOption, termMonths);

    // Not an offered combination. Clear the quote rather than show a price the
    // purchase endpoint would reject.
    if (!quote) {
      setCalculation(null);
      return;
    }

    setCalculation({
      downPayment: Math.round(quote.downPayment * 100) / 100,
      downPaymentPercentage: toNumberOrNull(downPaymentOption),
      processingFee,
      dueAtSigning: Math.round(quote.dueAtSigning * 100) / 100,
      feeFinanced: quote.feeFinanced,
      principal: Math.round(quote.principal * 100) / 100,
      interestRate: quote.interestRate,
      termMonths,
      monthlyPayment: Math.round(quote.monthlyPayment * 100) / 100,
      totalAmount: Math.round(quote.totalAmount * 100) / 100
    });
  }, [quoteFor, downPaymentOption, termMonths, processingFee]);

  useEffect(() => {
    if (property) {
      calculatePayment();
    }
  }, [property, downPaymentOption, termMonths, calculatePayment]);

  // useCallback only so the restore effect below can depend on it without
  // re-attaching a card form on every render. The body is unchanged.
  const initializeSquarePayment = useCallback(async () => {
    if (!window.Square) {
      // SDK not loaded yet - this might be a timing issue
      if (!squareLoaded) {
        setPurchaseError('Payment system is still loading. Please wait a moment and try again.');
      } else {
        setPurchaseError('Payment system failed to load. Please refresh the page.');
      }
      return;
    }

    try {
      // Use tenant's Square credentials
      const appId = getSquareApplicationId();
      const locationId = getSquareLocationId();

      console.log('[Square] Initializing with appId:', appId, 'locationId:', locationId);

      if (!appId || !locationId) {
        setPurchaseError('Payment processing is not configured. Please contact support.');
        return;
      }

      const payments = window.Square.payments(appId, locationId);

      const card = await payments.card();
      await card.attach('#card-container');
      setCardInstance(card);
      console.log('[Square] Card form initialized successfully');
    } catch (error) {
      console.error('Square initialization error:', error);
      setPurchaseError(`Failed to initialize payment form: ${error.message || error}`);
    }
  }, [squareLoaded, getSquareApplicationId, getSquareLocationId]);

  // BRING THE CARD FORM BACK UP after a restore, so the buyer returns to the
  // checkout they left rather than to a page that looks like it forgot them.
  //
  // This cannot happen in the restore effect above. initializeSquarePayment
  // attaches to #card-container, and that element is only rendered once a quote
  // has been calculated -- so the attach has to wait for `calculation`. It also
  // waits for `squareLoaded`, which doubles as the credentials check: the SDK
  // loader bails out early when the tenant has no Square configuration, so
  // squareLoaded can never be true for a tenant whose #card-container is not
  // on the page at all.
  useEffect(() => {
    if (!restoreCardPending) return;
    if (cardInstance) return;
    if (!calculation || !squareLoaded) return;
    // Cleared before the await, not after, so a re-render mid-attach cannot
    // queue a second card form onto the same container.
    setRestoreCardPending(false);
    initializeSquarePayment();
  }, [restoreCardPending, cardInstance, calculation, squareLoaded, initializeSquarePayment]);

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      // AUTH AT CHECKOUT. The buyer has already filled this in, so park it
      // before the redirect unmounts the page, and tell the login page both
      // where to come back to and why the buyer is standing there.
      savePurchaseDraft(id, {
        billingInfo,
        deedInfo,
        paymentDueDay,
        downPaymentOption,
        termMonths,
        desiredPayment
      });
      navigate('/login', {
        state: {
          from: location,
          message: 'Log in or create your account to complete this purchase. Your details are saved.'
        }
      });
      return;
    }

    // Validate terms accepted
    if (!termsAccepted) {
      setPurchaseError('You must read and accept the Terms of Service to complete this purchase');
      return;
    }

    // Validate billing info
    if (!billingInfo.name || !billingInfo.phone || !billingInfo.address || !billingInfo.city || !billingInfo.state || !billingInfo.zip) {
      setPurchaseError('Please fill in all billing information fields.');
      return;
    }

    // Validate deed info
    if (!deedInfo.deedName || !deedInfo.deedMailingAddress) {
      setPurchaseError('Please fill in deed name and mailing address.');
      return;
    }

    if (!cardInstance) {
      await initializeSquarePayment();
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;

    setPurchasing(true);
    setPurchaseError('');

    try {
      // Tokenize card
      const result = await cardInstance.tokenize();
      
      if (result.status === 'OK') {
        // Create loan with payment
        await createLoan({
          propertyId: property.id,
          downPaymentPercentage: parseFloat(downPaymentOption),
          termMonths: termMonths,
          paymentNonce: result.token,
          phone: billingInfo.phone,
          paymentDueDay: parseInt(paymentDueDay),
          deedName: deedInfo.deedName,
          deedMailingAddress: deedInfo.deedMailingAddress
        });

        // Bought. Nothing left to resume, here or on any other listing.
        clearPurchaseDrafts();

        alert('✅ Purchase Successful!\n\nYour property purchase has been completed and your loan has been created.\n\nRedirecting to your dashboard...');
        navigate('/dashboard');
      } else {
        setPurchaseError(result.errors?.[0]?.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setPurchaseError(error.response?.data?.details || error.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasing(false);
      submitLockRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading property...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="property-detail">
        <div className="error-message">{error || 'Property not found'}</div>
      </div>
    );
  }

  return (
    <div className="property-detail">
      <h1>{property.title}</h1>
      
      <div className="property-detail-grid">
        <div>
          {/* Image Gallery */}
          {images.length > 0 ? (
            <div style={{ marginBottom: '1rem' }}>
              {/* Main Image */}
              <img
                src={getCloudinaryTransform(images[selectedImageIndex].url, 800, 400)}
                alt={images[selectedImageIndex].caption || property.title}
                loading="eager"
                className="property-main-image"
              />
              
              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="property-thumbnail-grid">
                  {images.map((image, index) => (
                    <img
                      key={image.id}
                      src={getCloudinaryTransform(image.url, 100, 100)}
                      alt={image.caption || `View ${index + 1}`}
                      onClick={() => setSelectedImageIndex(index)}
                      loading="lazy"
                      className={`property-thumbnail ${selectedImageIndex === index ? 'selected' : ''}`}
                    />
                  ))}
                </div>
              )}
              
              {/* Caption */}
              {images[selectedImageIndex].caption && (
                <p className="image-caption">
                  {images[selectedImageIndex].caption}
                </p>
              )}
            </div>
          ) : (
            <img
              src={getCloudinaryTransform("https://res.cloudinary.com/dxd4ef2tc/image/upload/IMAGES-COMING-SOON_tbspdc.png", 800, 400)}
              alt="Images Coming Soon"
              loading="lazy"
              className="property-main-image"
              style={{ objectFit: 'contain', background: 'var(--color-surface-sunken)' }}
            />
          )}
          
          {/* Photo Disclaimer */}
          <div className="photo-disclaimer">
            📷 Note: Photos shown are representative of the surrounding area and may not depict the exact parcel for sale.
          </div>
          
          <div className="card">
            <h2>Property Details</h2>
            <p>{property.description}</p>
            
            <div className="property-info-grid">
              <div className="property-info-item">
                <span className="property-info-label">Location</span>
                <strong className="property-info-value">{property.location}</strong>
              </div>
              <div className="property-info-item">
                <span className="property-info-label">County</span>
                <strong className="property-info-value">{property.county}</strong>
              </div>
              <div className="property-info-item">
                <span className="property-info-label">State</span>
                <strong className="property-info-value">{property.state}</strong>
              </div>
              <div className="property-info-item">
                <span className="property-info-label">Acres</span>
                <strong className="property-info-value">{property.acres}</strong>
              </div>
              {property.annual_tax_amount && (
                <div className="property-info-item">
                  <span className="property-info-label">Annual Property Tax</span>
                  <strong className="property-info-value">${formatCurrency(property.annual_tax_amount)}</strong>
                </div>
              )}
              {property.monthly_hoa_fee && (
                <div className="property-info-item">
                  <span className="property-info-label">Monthly HOA Fee</span>
                  <strong className="property-info-value">${formatCurrency(property.monthly_hoa_fee)}</strong>
                </div>
              )}
              {property.apn && (
                <div className="property-info-item">
                  <span className="property-info-label">APN</span>
                  <strong className="property-info-value">{property.apn}</strong>
                </div>
              )}
            </div>

            {/* GPS Coordinates Section */}
            {property.coordinates && (() => {
              try {
                const coords = JSON.parse(property.coordinates);
                const hasCoords = coords.ne_corner || coords.se_corner || coords.sw_corner || coords.nw_corner || coords.center;
                
                if (!hasCoords) return null;
                
                return (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--light-green)', borderRadius: '8px' }}>
                    <strong style={{ color: 'var(--color-brand-text)', fontSize: '1.1rem' }}>📍 GPS Coordinates</strong>
                    <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                      {coords.ne_corner && (
                        <div>
                          <strong>NE Corner:</strong> {coords.ne_corner}
                          {' '}
                          <a 
                            href={`https://www.google.com/maps?q=${coords.ne_corner}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-brand-text)', fontSize: '0.85rem' }}
                          >
                            [View on Map]
                          </a>
                        </div>
                      )}
                      {coords.se_corner && (
                        <div>
                          <strong>SE Corner:</strong> {coords.se_corner}
                          {' '}
                          <a 
                            href={`https://www.google.com/maps?q=${coords.se_corner}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-brand-text)', fontSize: '0.85rem' }}
                          >
                            [View on Map]
                          </a>
                        </div>
                      )}
                      {coords.sw_corner && (
                        <div>
                          <strong>SW Corner:</strong> {coords.sw_corner}
                          {' '}
                          <a 
                            href={`https://www.google.com/maps?q=${coords.sw_corner}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-brand-text)', fontSize: '0.85rem' }}
                          >
                            [View on Map]
                          </a>
                        </div>
                      )}
                      {coords.nw_corner && (
                        <div>
                          <strong>NW Corner:</strong> {coords.nw_corner}
                          {' '}
                          <a 
                            href={`https://www.google.com/maps?q=${coords.nw_corner}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-brand-text)', fontSize: '0.85rem' }}
                          >
                            [View on Map]
                          </a>
                        </div>
                      )}
                      {coords.center && (
                        <div>
                          <strong>Center Point:</strong> {coords.center}
                          {' '}
                          <a 
                            href={`https://www.google.com/maps?q=${coords.center}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-brand-text)', fontSize: '0.85rem' }}
                          >
                            [View on Map]
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } catch {
                return null;
              }
            })()}

            {property.features && property.features.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <strong>Features:</strong>
                <div className="property-features">
                  {property.features.map((feature, index) => (
                    <span key={index} className="feature-tag">{feature}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="price-box">
            <h2>${formatCurrency(property.price)}</h2>
            <p>{property.acres} acres</p>
          </div>

          {property.status === 'coming_soon' ? (
            <div className="calculator">
              <h3>🎉 Coming Soon!</h3>
              <div className="coming-soon-container">
                <div className="coming-soon-icon">📍</div>
                <h4 className="coming-soon-title">This Property is Coming Soon!</h4>
                <p className="coming-soon-text">
                  We've recently acquired this property and are currently waiting for the deed transfer to complete. 
                  This property will be available for purchase soon!
                </p>
                <div className="coming-soon-price-box">
                  <div className="coming-soon-price-label">Estimated Price</div>
                  <div className="coming-soon-price">${formatCurrency(property.price)}</div>
                </div>
                <p className="coming-soon-footer">
                  💡 Check back soon to see when this property becomes available!
                </p>
              </div>
            </div>
          ) : (
            <div className="calculator">
              <h3>💰 Find Your Perfect Payment Plan</h3>
              
              {/* INPUTS AT TOP */}
              <div className="calculator-options">
                <div>
                  <label>What monthly payment works for you? (Optional)</label>
                  <div className="amount-input-group">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      placeholder="150"
                      min="50"
                      step="1"
                      value={desiredPayment}
                      onChange={(e) => setDesiredPayment(e.target.value)}
                    />
                  </div>
                  <small style={{ display: 'block', color: 'var(--color-text-light)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    Minimum ${minimumPayment}/month • Enter your ideal payment to see which option fits best
                  </small>
                </div>

                <div>
                  <label>Loan Term:</label>
                  <select
                    value={termMonths}
                    onChange={(e) => setTermMonths(parseInt(e.target.value))}
                  >
                    {termMonthsOptions.map(months => (
                      <option key={months} value={months}>
                        {months / 12} {months === 12 ? 'Year' : 'Years'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SELECTED PLAN SUMMARY */}
              {calculation && (
                <div className="plan-summary-box">
                  <div className="plan-summary-title">Your Selected Plan:</div>
                  <div className="plan-summary-grid">
                    <div>Down Payment: ${formatCurrency(calculation.downPayment)}</div>
                    <div>
                      Processing Fee: ${formatCurrency(processingFee)}
                      {calculation.feeFinanced ? ' (included in your loan)' : ' (due at signing)'}
                    </div>
                    <div>Monthly Payment: ${formatCurrency(calculation.monthlyPayment)}</div>
                    <div>Total Cost (Includes Loan Interest): ${formatCurrency(calculation.totalAmount)}</div>
                  </div>
                </div>
              )}

              {/* PAYMENT OPTIONS */}
              <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <h4 style={{ color: 'var(--color-brand-text)', marginBottom: '1rem' }}>Choose Your Down Payment Option:</h4>
                {desiredPayment && desiredPayment >= minimumPayment && closestOption && (() => {
                  const closestQuote = quoteFor(closestOption, termMonths);
                  if (!closestQuote) return null;

                  const closestPayment = closestQuote.monthlyPayment;
                  const closestLabel = closestQuote.kind === 'fixed'
                    ? `$${closestQuote.downPayment} Down`
                    : `${closestOption}% Down`;

                  const difference = Math.abs(parseFloat(desiredPayment) - closestPayment);
                  const isGoodMatch = difference <= 30;
                  const isTooLow = parseFloat(desiredPayment) < closestPayment;
                  const longestTerm = termMonthsOptions.length ? Math.max(...termMonthsOptions) : termMonths;
                  const shortestTerm = termMonthsOptions.length ? Math.min(...termMonthsOptions) : termMonths;

                  if (isGoodMatch) {
                    return (
                      <div className="closest-match-success">
                        ✓ The <strong>{closestLabel}</strong> option is closest to your ${formatCurrency(desiredPayment)}/month goal!
                      </div>
                    );
                  } else {
                    return (
                      <div className="closest-match-warning">
                        ⚠️ Your desired payment of ${formatCurrency(desiredPayment)}/month is {isTooLow ? 'lower' : 'higher'} than our available options.
                        The closest we can offer is <strong>${formatCurrency(closestPayment)}/month</strong> with {closestLabel}.
                        {isTooLow && termMonths < longestTerm && <div style={{ marginTop: '0.5rem' }}>💡 Try selecting a longer loan term to lower your monthly payment!</div>}
                        {!isTooLow && termMonths > shortestTerm && <div style={{ marginTop: '0.5rem' }}>💡 Try selecting a shorter loan term or you may be able to pay off early with no penalty!</div>}
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Fixed-amount down payment option (the "$99 down" style special) */}
              {(() => {
                if (!fixedOption) return null;
                const fixedQuote = quoteFor(fixedOption, termMonths);
                if (!fixedQuote) return null;
                return (
                  <div
                    className={`payment-option-card ${downPaymentOption === fixedOption ? 'selected' : ''} ${closestOption === fixedOption && downPaymentOption !== fixedOption ? 'highlighted' : ''}`}
                    onClick={() => setDownPaymentOption(fixedOption)}
                  >
                    <div className="payment-option-header">
                      <div>
                        <div className="payment-option-title">
                          ${fixedQuote.downPayment} Down Payment
                          {fixedQuote.tier.isPopular && <span className="payment-option-badge">MOST POPULAR</span>}
                        </div>
                        <div className="payment-option-rate">{fixedQuote.interestRate}% APR</div>
                      </div>
                      <div className="payment-option-amount">
                        <div className="payment-option-price">
                          ${formatCurrency(fixedQuote.monthlyPayment)}
                        </div>
                        <div className="payment-option-frequency">/month</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Percentage options - exactly the tiers this business has configured */}
              {percentOptions.map(option => {
                const quote = quoteFor(option, termMonths);
                if (!quote) return null;
                const { downPayment, interestRate, monthlyPayment } = quote;

                return (
                  <div
                    key={option}
                    className={`payment-option-card ${downPaymentOption === option ? 'selected' : ''} ${closestOption === option && downPaymentOption !== option ? 'highlighted' : ''}`}
                    onClick={() => setDownPaymentOption(option)}
                  >
                    <div className="payment-option-header">
                      <div>
                        <div className="payment-option-title">
                          {option}% Down (${formatCurrency(downPayment)})
                          {/* The MOST POPULAR badge belongs to whichever tier carries
                              isPopular, not to a particular KIND of tier. This branch
                              had no badge markup at all, so moving the flag onto a
                              percentage tier made it vanish from the page entirely --
                              it looked like the save had dropped the flag when the
                              save, the storage and the published payload were all
                              correct and only the render was blind to it. */}
                          {quote.tier.isPopular && <span className="payment-option-badge">MOST POPULAR</span>}
                        </div>
                        <div className="payment-option-rate">{interestRate}% APR</div>
                      </div>
                      <div className="payment-option-amount">
                        <div className="payment-option-price">
                          ${formatCurrency(monthlyPayment)}
                        </div>
                        <div className="payment-option-frequency">/month</div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* READY TO PURCHASE SECTION */}
              {calculation && (
                <>
                  <div className="purchase-summary-container">
                    <h4 className="purchase-summary-title">Ready to Purchase?</h4>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <div className="cost-today-box">
                        <div className="cost-today-label">Your Cost Today</div>
                        <div className="cost-today-amount">
                          ${formatCurrency(calculation.dueAtSigning)}
                        </div>
                        <div className="cost-today-breakdown">
                          {calculation.feeFinanced
                            ? '($' + formatCurrency(calculation.downPayment) + ' down. The $'
                              + formatCurrency(processingFee) + ' processing fee is included in your loan, not due today)'
                            : '($' + formatCurrency(calculation.downPayment) + ' down + $'
                              + formatCurrency(processingFee) + ' processing fee)'}
                        </div>
                      </div>
                      
                      <div className="purchase-plan-details">Your Selected Plan:</div>
                      <div className="purchase-plan-grid">
                        <div className="purchase-plan-item">
                          <span className="purchase-plan-label">Down Payment:</span>
                          <strong className="purchase-plan-value">${formatCurrency(calculation.downPayment)}</strong>
                        </div>
                        <div className="purchase-plan-item">
                          <span className="purchase-plan-label">
                            Processing Fee:
                            {calculation.feeFinanced ? ' (in your loan)' : ' (due today)'}
                          </span>
                          <strong>${formatCurrency(processingFee)}</strong>
                        </div>
                        <div className="purchase-plan-item">
                          <span className="purchase-plan-label">Monthly Payment:</span>
                          <strong className="purchase-plan-value">${formatCurrency(calculation.monthlyPayment)}</strong>
                        </div>
                        <div className="purchase-plan-item">
                          <span className="purchase-plan-label">Total Cost<br />(Includes Loan Interest):</span>
                          <strong>${formatCurrency(calculation.totalAmount)}</strong>
                        </div>
                      </div>
                      
                      <div className="payment-schedule-note">
                        📅 <strong>{calculation.termMonths} monthly payments</strong> of ${formatCurrency(calculation.monthlyPayment)}
                      </div>
                    </div>

                    {cardInstance && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h5 style={{ marginBottom: '0.5rem', color: 'var(--color-brand-text)' }}>Billing Information</h5>
                        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '1rem' }}>
                          <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span> Required fields
                        </p>
                        
                        {/* NO PICKER IN ANNIVERSARY MODE. The tenant offers no
                            list of days: the due day is the day of the month this
                            purchase completes, and the server derives it from the
                            closing date without reading paymentDueDay off the
                            body at all. Showing a choice that is discarded is the
                            defect Stage 2a found on this very control. */}
                        {isAnniversaryDueDay ? (
                          <div className="payment-date-selector">
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '600', color: 'var(--color-brand-text)' }}>
                              📅 Your Monthly Payment Date
                            </label>
                            <p style={{ fontSize: '0.95rem', color: 'var(--color-text)', margin: 0, lineHeight: 1.5 }}>
                              Payments are due on the anniversary of your purchase date, the same
                              day of each month that you buy today. In any month without that day,
                              your payment falls on the last day of the month.
                            </p>
                          </div>
                        ) : (
                        <div className="payment-date-selector">
                          <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600', color: 'var(--color-brand-text)' }}>
                            📅 Choose Your Monthly Payment Date <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                          </label>
                          {/* Driven by the tenant's configured paymentDueDayOptions.
                              These were two hardcoded 1/15 tiles, so a tenant
                              offering other days could not sell them -- and the
                              server rejects any day it does not offer. */}
                          <div className="payment-date-options">
                            {offeredDueDays.map(day => (
                              <div
                                key={day}
                                onClick={() => setPaymentDueDay(String(day))}
                                className={`payment-date-option ${paymentDueDay === String(day) ? 'selected' : ''}`}
                              >
                                <div className="payment-date-number">{formatOrdinalDay(day)}</div>
                                <div className="payment-date-label">of Each Month</div>
                              </div>
                            ))}
                          </div>
                          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginTop: '0.75rem', textAlign: 'center' }}>
                            💡 Choose the date that works best with your budget
                          </p>
                        </div>
                        )}
                        
                        <div className="form-group">
                          <label>
                            Phone Number <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                          </label>
                          <input
                            type="tel"
                            value={billingInfo.phone}
                            onChange={(e) => setBillingInfo({...billingInfo, phone: e.target.value})}
                            placeholder="(555) 123-4567"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            Cardholder Name <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={billingInfo.name}
                            onChange={(e) => setBillingInfo({...billingInfo, name: e.target.value})}
                            placeholder="John Smith"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>
                            Billing Address <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                          </label>
                          <input
                            type="text"
                            value={billingInfo.address}
                            onChange={(e) => setBillingInfo({...billingInfo, address: e.target.value})}
                            placeholder="123 Main St"
                            required
                          />
                        </div>

                        <div className="form-row-wide-2">
                          <div className="form-group">
                            <label>
                              City <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={billingInfo.city}
                              onChange={(e) => setBillingInfo({...billingInfo, city: e.target.value})}
                              placeholder="Appleton"
                              required
                            />
                          </div>
                          
                          <div className="form-group">
                            <label>
                              State <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                            </label>
                            <select
                              value={billingInfo.state}
                              onChange={(e) => setBillingInfo({...billingInfo, state: e.target.value})}
                              required
                            >
                              <option value="">--</option>
                              <option value="WI">WI</option>
                              <option value="AZ">AZ</option>
                              <option value="AR">AR</option>
                              <option value="CO">CO</option>
                              <option value="AL">AL</option>
                              <option value="AK">AK</option>
                              <option value="CA">CA</option>
                              <option value="FL">FL</option>
                              <option value="GA">GA</option>
                              <option value="IL">IL</option>
                              <option value="IN">IN</option>
                              <option value="IA">IA</option>
                              <option value="KS">KS</option>
                              <option value="KY">KY</option>
                              <option value="LA">LA</option>
                              <option value="MI">MI</option>
                              <option value="MN">MN</option>
                              <option value="MO">MO</option>
                              <option value="NE">NE</option>
                              <option value="NV">NV</option>
                              <option value="NY">NY</option>
                              <option value="NC">NC</option>
                              <option value="OH">OH</option>
                              <option value="OK">OK</option>
                              <option value="OR">OR</option>
                              <option value="PA">PA</option>
                              <option value="TX">TX</option>
                              <option value="UT">UT</option>
                              <option value="WA">WA</option>
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label>
                              ZIP <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={billingInfo.zip}
                              onChange={(e) => setBillingInfo({...billingInfo, zip: e.target.value})}
                              placeholder="54911"
                              required
                              maxLength="5"
                            />
                          </div>
                        </div>

                        <div className="deed-info-box">
                          <h5>📜 Deed Information</h5>
                          <p style={{ fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '15px' }}>
                            This information will appear on the property deed when your loan is paid off.
                          </p>
                          
                          <div className="form-group">
                            <label>
                              Name(s) for Deed <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={deedInfo.deedName}
                              onChange={(e) => setDeedInfo({...deedInfo, deedName: e.target.value})}
                              placeholder="John and Jane Smith"
                              required
                            />
                            <small style={{ display: 'block', color: 'var(--color-text-light)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                              Enter name(s) exactly as you want them to appear on the deed
                            </small>
                          </div>

                          <div className="form-group">
                            <label>
                              Mailing Address for Deed <span style={{ color: 'var(--color-error)', fontWeight: 'bold' }}>*</span>
                            </label>
                            <textarea
                              rows="3"
                              value={deedInfo.deedMailingAddress}
                              onChange={(e) => setDeedInfo({...deedInfo, deedMailingAddress: e.target.value})}
                              placeholder="123 Main St&#10;Appleton, WI 54911"
                              required
                            />
                            <small style={{ display: 'block', color: 'var(--color-text-light)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                              Full address where the deed should be mailed upon loan payoff
                            </small>
                          </div>
                        </div>

                        <div style={{ marginTop: '1rem' }}>
                          <h5 style={{ marginBottom: '0.75rem', color: 'var(--color-brand-text)' }}>Card Information</h5>
                        </div>
                      </div>
                    )}

                    {/* Terms of Service */}
                    {cardInstance && (
                      <div className={`terms-accepted-box ${termsAccepted ? 'accepted' : 'not-accepted'}`}>
                        <div style={{ marginBottom: '10px', fontWeight: '600' }}>
                          {termsAccepted ? '✓ Terms Accepted' : '⚠️ Required: Terms of Service'}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="btn"
                          style={{
                            width: '100%',
                            backgroundColor: termsAccepted ? '#28a745' : '#ffc107',
                            color: termsAccepted ? 'white' : '#000',
                            padding: '12px',
                            fontSize: '16px',
                            fontWeight: 'bold'
                          }}
                        >
                          {termsAccepted ? '✓ Terms of Service Accepted' : 'Click to Read & Accept Terms'}
                        </button>
                      </div>
                    )}

                    {/* Due Diligence Agreement */}
                    {cardInstance && (
                      <div className="due-diligence-box">
                        <h5>⚠️ Due Diligence Statement</h5>
                        <p style={{ fontSize: '0.85rem', color: 'var(--color-warning-text)', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                          All prospective buyers are urged to conduct their own due diligence to their satisfaction prior to purchasing this property. 
                          Prospective purchasers are strongly encouraged to examine, visit, and thoroughly research the property before buying. 
                          This includes verifying boundaries, utilities, zoning, road access, and suitability for your intended use.
                        </p>
                        <p style={{ fontSize: '0.85rem', color: 'var(--color-warning-text)', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                          All information contained in this listing has come from reliable sources and is believed to be accurate to the best of our knowledge.
                          However, <strong>{getCompanyLegalName()}</strong> makes no guarantee, expressed or implied, as to the location, condition,
                          accessibility, terrain, buildability, utility availability, or any other information contained in this listing.
                        </p>
                        <div className="due-diligence-checkbox-wrapper">
                          <input
                            type="checkbox"
                            id="dueDiligenceCheckbox"
                            checked={dueDiligenceAgreed}
                            onChange={(e) => setDueDiligenceAgreed(e.target.checked)}
                          />
                          <label htmlFor="dueDiligenceCheckbox">
                            <strong>I acknowledge that I have read and understand the Due Diligence Statement above. 
                            I have conducted adequate due diligence and accept the property in its current condition.</strong>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* Check if payment processing is configured */}
                    {!hasCustomSquareCredentials() ? (
                      <div style={{
                        padding: '20px',
                        background: 'var(--color-warning-bg)',
                        border: '1px solid var(--color-warning-border)',
                        borderRadius: '8px',
                        marginTop: '1rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.5rem' }}>📞</span>
                          <strong style={{ color: 'var(--color-warning-text)' }}>Online payments are not currently available</strong>
                        </div>
                        <p style={{ margin: 0, color: 'var(--color-warning-text)' }}>
                          Please contact us directly for payment options:
                          <br />
                          <strong>Phone:</strong> {getCompanyPhone()}
                          <br />
                          <strong>Email:</strong> {getCompanyEmail()}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div id="card-container" style={{ display: cardInstance ? 'block' : 'none', marginBottom: '1rem' }}></div>

                        {purchaseError && (
                          <div className="error-message" style={{ marginTop: '1rem' }}>
                            {purchaseError}
                          </div>
                        )}

                        <button
                          className="btn btn-primary btn-full-width"
                          onClick={cardInstance ? handlePurchase : initializeSquarePayment}
                          disabled={purchasing || (cardInstance && (!dueDiligenceAgreed || !termsAccepted))}
                          style={{ marginTop: '1rem' }}
                        >
                          {purchasing
                            ? 'Processing...'
                            : cardInstance
                              ? 'Pay $' + formatCurrency(calculation.dueAtSigning) + ' Now'
                              : isAuthenticated
                                ? 'Purchase This Property'
                                // Browse freely, auth at checkout: this opens
                                // the checkout form, it does not ask for a
                                // login, and the label now says so. It read
                                // "Login to Purchase" while the click went
                                // straight to the payment form.
                                : 'Continue to Checkout'
                          }
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="terms-modal-overlay">
          <div className="terms-modal-content">
            <h2>Terms of Service</h2>
            
            <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--color-text)' }}>
              <h3>Agreement to Terms</h3>
              <p>By purchasing property, you agree to be bound by these Terms of Service.</p>
              
              <h3>Contract for Deed</h3>
              <p>All land purchases are made through a Contract for Deed. Legal title remains with {getCompanyLegalName()} until the contract is paid in full. You receive equitable title and can use the property according to contract terms.</p>
              
              <h3>Payment Terms</h3>
              <ul>
                <li>
                  {isAnniversaryDueDay
                    ? 'Monthly payments are due on the anniversary of your purchase date'
                    : `Monthly payments must be made on your selected due date (${formatDueDayOptions(offeredDueDays)})`}
                </li>
                <li>Late payments ({getGracePeriodDays()}+ days past due) incur a late fee</li>
                <li>Payments include principal, interest, and may include property taxes and HOA fees</li>
                <li>You may prepay without penalty at any time</li>
              </ul>
              
              <h3>Default and Forfeiture</h3>
              <p><strong style={{ color: 'var(--color-error)' }}>⚠️ CRITICAL:</strong> If you default on payments:</p>
              <ul>
                <li>You will receive written notice providing {getCurePeriodDays()} days to cure the default</li>
                <li>If not cured, <strong>all payments made will be forfeited</strong> (considered rent for use of property)</li>
                <li>You will immediately lose all rights to the property</li>
                <li>No refund of any payments or down payment</li>
              </ul>
              
              <h3>Property "As-Is"</h3>
              <p><strong>Properties are sold "AS IS" without warranty of any kind.</strong> You are responsible for conducting your own due diligence including property inspections, title searches, and verification of utilities, access, zoning, and buildability.</p>
              
              <h3>Governing Law</h3>
              <p>These Terms shall be governed by the laws of the State of Wisconsin. Any disputes shall be resolved exclusively in the courts of Outagamie County, Appleton, Wisconsin.</p>
              
              <p style={{ marginTop: '20px', padding: '10px', background: 'var(--color-surface-sunken)', borderRadius: '5px' }}>
                <strong>Contact:</strong> {getCompanyEmail()} | {getCompanyPhone()}
              </p>
              
              <p style={{ textAlign: 'center', marginTop: '15px', fontSize: '12px', color: 'var(--color-text-light)' }}>
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand-text)' }}>View Full Terms of Service (Opens in New Window)</a>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                className="btn"
                style={{
                  flex: 1,
                  backgroundColor: 'var(--forest-green)',
                  color: 'white',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                ✓ I Accept the Terms of Service
              </button>
              <button
                onClick={() => setShowTermsModal(false)}
                className="btn"
                style={{
                  flex: 1,
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyDetail;